/**
 * Redux slice de la discussion « sous la note » (chantier C10, SP1, spec § 2.5).
 *
 * L'état est indexé **par note** (`byNoteId`) : deux discussions peuvent être
 * montées en même temps — le dock de `ModalNote` sur la note A et
 * `NotePreviewModal` (ouverte depuis une `[[référence]]` ou un rétrolien) sur la
 * note B — sans que l'une écrase l'autre. Chaque entrée porte son propre fil,
 * son propre statut et sa propre erreur ; une garde `requestId` ne sert plus
 * qu'à écarter la réponse périmée d'un fetch de la *même* note.
 * Pas de mise à jour optimiste : le fil est court, on attend le serveur.
 */
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { noteCommentsService } from '../../services/noteCommentsAPI';
import { reactionsService } from '../../services/reactionsAPI';
import { getTokenRedirect } from '../../utils/authRedirect';
import { tokenRequest } from '../../authConfig';
import { LOADING_STATES } from '../../constants';
import type { LoadingState } from '../../types/redux';
import type { CitationDeQuestion, NoteCommentDto, NoteCommentsPayload, ReactionCounts } from '../../types/api';

/** État de la discussion d'UNE note. */
export interface NoteCommentsEntry {
    comments: NoteCommentDto[];
    canComment: boolean;
    /** Le propriétaire de la note : seul à pouvoir répondre à une question en reliant le mot (C23 SP3). */
    canAnswer: boolean;
    count: number;
    status: LoadingState;
    /** Une publication/modification/suppression est en cours sur cette note. */
    submitting: boolean;
    error: string | null;
    /** Dernier chargement lancé pour cette note : garde anti-course intra-note. */
    requestId: string | null;
    /** Réactions sur la note elle-même (chantier C10, SP3). */
    noteReactions: ReactionCounts;
}

export interface NoteCommentsState {
    byNoteId: Record<string, NoteCommentsEntry>;
}

/** Entrée neuve — jamais mutée : les reducers en clonent une copie. */
export const emptyNoteCommentsEntry = (): NoteCommentsEntry => ({
    comments: [],
    canComment: false,
    canAnswer: false,
    count: 0,
    status: LOADING_STATES.IDLE,
    submitting: false,
    error: null,
    requestId: null,
    noteReactions: {},
});

export const initialNoteCommentsState: NoteCommentsState = { byNoteId: {} };

const EMPTY: NoteCommentDto[] = [];
/** Entrée de repli des sélecteurs : référence stable, sinon re-rendus en boucle. */
const EMPTY_ENTRY: NoteCommentsEntry = Object.freeze({
    comments: EMPTY,
    canComment: false,
    canAnswer: false,
    count: 0,
    status: LOADING_STATES.IDLE,
    submitting: false,
    error: null,
    requestId: null,
    noteReactions: {},
}) as NoteCommentsEntry;
/** Réactions de repli des sélecteurs : référence stable, sinon re-rendus en boucle. */
const EMPTY_COUNTS: ReactionCounts = Object.freeze({});

async function tokenOf(instance: any): Promise<string> {
    const accessToken = await getTokenRedirect(tokenRequest, instance);
    if (!accessToken) throw new Error('Impossible d\'obtenir le token d\'accès');
    return accessToken;
}

/** Message d'un échec axios : celui du serveur (403/404) sinon générique. */
function messageOf(error: any, fallback: string): string {
    return error?.response?.data?.message || error?.message || fallback;
}

export const fetchNoteComments = createAsyncThunk<NoteCommentsPayload, { noteId: string; instance: any }, { rejectValue: string }>(
    'noteComments/fetch',
    async ({ noteId, instance }, { rejectWithValue }) => {
        try {
            return await noteCommentsService.list(noteId, await tokenOf(instance));
        } catch (error) {
            return rejectWithValue(messageOf(error, 'Impossible de charger la discussion'));
        }
    },
);

/** `citation` fait du commentaire une question sur un mot (C23 SP3) ; le message du serveur (400, 409) remonte tel quel. */
export const addNoteComment = createAsyncThunk<NoteCommentDto, { noteId: string; text: string; parentId?: string | null; citation?: CitationDeQuestion; instance: any }, { rejectValue: string }>(
    'noteComments/add',
    async ({ noteId, text, parentId, citation, instance }, { rejectWithValue }) => {
        try {
            return await noteCommentsService.create(noteId, { text, parentId, ...(citation ? { citation } : {}) }, await tokenOf(instance));
        } catch (error) {
            return rejectWithValue(messageOf(error, 'Impossible de publier le commentaire'));
        }
    },
);

export const updateNoteComment = createAsyncThunk<NoteCommentDto, { noteId: string; commentId: string; text: string; instance: any }, { rejectValue: string }>(
    'noteComments/update',
    async ({ noteId, commentId, text, instance }, { rejectWithValue }) => {
        try {
            return await noteCommentsService.update(noteId, commentId, text, await tokenOf(instance));
        } catch (error) {
            return rejectWithValue(messageOf(error, 'Impossible de modifier le commentaire'));
        }
    },
);

/** Supprime puis recharge : la suppression peut être douce ou dure, le serveur seul le sait. */
export const deleteNoteComment = createAsyncThunk<NoteCommentsPayload, { noteId: string; commentId: string; instance: any }, { rejectValue: string }>(
    'noteComments/delete',
    async ({ noteId, commentId, instance }, { rejectWithValue }) => {
        try {
            const token = await tokenOf(instance);
            await noteCommentsService.remove(noteId, commentId, token);
            return await noteCommentsService.list(noteId, token);
        } catch (error) {
            return rejectWithValue(messageOf(error, 'Impossible de supprimer le commentaire'));
        }
    },
);

/** Bascule une réaction sur la note ou un commentaire (chantier C10, SP3). */
export const toggleReaction = createAsyncThunk<
    { targetType: 'note' | 'comment'; targetId: string; counts: ReactionCounts },
    { noteId: string; targetType: 'note' | 'comment'; targetId: string; emoji: string; instance: any },
    { rejectValue: string }
>('noteComments/toggleReaction', async ({ targetType, targetId, emoji, instance }, { rejectWithValue }) => {
    try {
        const result = await reactionsService.toggle({ targetType, targetId, emoji }, await tokenOf(instance));
        return { targetType, targetId, counts: result.counts };
    } catch (error) {
        return rejectWithValue(messageOf(error, 'Impossible d\'enregistrer la réaction'));
    }
});

/** Entrée de `noteId`, créée à la volée : tout reducer écrit dans la sienne. */
const entryOf = (state: NoteCommentsState, noteId: string): NoteCommentsEntry => {
    if (!state.byNoteId[noteId]) state.byNoteId[noteId] = emptyNoteCommentsEntry();
    return state.byNoteId[noteId];
};

const applyPayload = (entry: NoteCommentsEntry, payload: NoteCommentsPayload | undefined) => {
    entry.comments = payload?.comments ?? [];
    entry.canComment = payload?.canComment ?? false;
    entry.canAnswer = payload?.canAnswer ?? false;
    entry.count = payload?.count ?? 0;
    entry.noteReactions = payload?.noteReactions ?? {};
};

const noteCommentsSlice = createSlice({
    name: 'noteComments',
    initialState: initialNoteCommentsState,
    reducers: {
        /** Fermeture de la modale : rien ne doit survivre à la note fermée. */
        resetNoteComments() {
            return { byNoteId: {} };
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchNoteComments.pending, (state, action) => {
                const entry = entryOf(state, action.meta.arg.noteId);
                entry.requestId = action.meta.requestId;
                entry.status = LOADING_STATES.LOADING;
                entry.error = null;
            })
            .addCase(fetchNoteComments.fulfilled, (state, action) => {
                const entry = state.byNoteId[action.meta.arg.noteId];
                // Réponse périmée d'un chargement plus ancien de la même note.
                if (!entry || entry.requestId !== action.meta.requestId) return;
                applyPayload(entry, action.payload);
                entry.status = LOADING_STATES.SUCCEEDED;
                entry.error = null;
            })
            .addCase(fetchNoteComments.rejected, (state, action) => {
                const entry = state.byNoteId[action.meta.arg.noteId];
                if (!entry || entry.requestId !== action.meta.requestId) return;
                entry.status = LOADING_STATES.FAILED;
                entry.error = action.payload || action.error?.message || 'Impossible de charger la discussion';
            })
            .addCase(addNoteComment.pending, (state, action) => {
                const entry = entryOf(state, action.meta.arg.noteId);
                entry.submitting = true;
                entry.error = null;
            })
            .addCase(addNoteComment.fulfilled, (state, action) => {
                const entry = entryOf(state, action.meta.arg.noteId);
                entry.submitting = false;
                entry.comments.push(action.payload);
                entry.count += 1;
            })
            .addCase(addNoteComment.rejected, (state, action) => {
                const entry = entryOf(state, action.meta.arg.noteId);
                entry.submitting = false;
                entry.error = action.payload || action.error?.message || 'Impossible de publier le commentaire';
            })
            .addCase(updateNoteComment.pending, (state, action) => {
                const entry = entryOf(state, action.meta.arg.noteId);
                entry.submitting = true;
                entry.error = null;
            })
            .addCase(updateNoteComment.fulfilled, (state, action) => {
                const entry = entryOf(state, action.meta.arg.noteId);
                entry.submitting = false;
                const index = entry.comments.findIndex(c => c.id === action.payload.id);
                // Le serveur ne renvoie pas les réactions sur une modification : on garde celles affichées.
                if (index >= 0) entry.comments[index] = { ...action.payload, reactions: entry.comments[index].reactions };
            })
            .addCase(updateNoteComment.rejected, (state, action) => {
                const entry = entryOf(state, action.meta.arg.noteId);
                entry.submitting = false;
                entry.error = action.payload || action.error?.message || 'Impossible de modifier le commentaire';
            })
            .addCase(deleteNoteComment.pending, (state, action) => {
                const entry = entryOf(state, action.meta.arg.noteId);
                entry.submitting = true;
                entry.error = null;
            })
            .addCase(deleteNoteComment.fulfilled, (state, action) => {
                const entry = entryOf(state, action.meta.arg.noteId);
                entry.submitting = false;
                applyPayload(entry, action.payload);
            })
            .addCase(deleteNoteComment.rejected, (state, action) => {
                const entry = entryOf(state, action.meta.arg.noteId);
                entry.submitting = false;
                entry.error = action.payload || action.error?.message || 'Impossible de supprimer le commentaire';
            })
            .addCase(toggleReaction.pending, (state, action) => {
                const entry = entryOf(state, action.meta.arg.noteId);
                entry.submitting = true;
                entry.error = null;
            })
            .addCase(toggleReaction.fulfilled, (state, action) => {
                const entry = entryOf(state, action.meta.arg.noteId);
                entry.submitting = false;
                const { targetType, targetId, counts } = action.payload;
                if (targetType === 'note') { entry.noteReactions = counts; return; }
                const comment = entry.comments.find(c => c.id === targetId);
                if (comment) comment.reactions = counts;
            })
            .addCase(toggleReaction.rejected, (state, action) => {
                const entry = entryOf(state, action.meta.arg.noteId);
                entry.submitting = false;
                entry.error = action.payload || action.error?.message || 'Impossible d\'enregistrer la réaction';
            });
    },
});

export const { resetNoteComments } = noteCommentsSlice.actions;

// Sélecteurs nul-sûrs : le store des tests de ModalNote peut ne pas porter ce slice.
const sliceOf = (state: any): NoteCommentsState | undefined => state?.noteComments;

/** Entrée de `noteId` — repli sur une entrée vide de référence stable. */
export const selectNoteCommentsEntry = (state: any, noteId: string | null | undefined): NoteCommentsEntry =>
    (noteId ? sliceOf(state)?.byNoteId?.[noteId] : undefined) ?? EMPTY_ENTRY;

export const selectNoteComments = (state: any, noteId: string | null | undefined): NoteCommentDto[] =>
    selectNoteCommentsEntry(state, noteId).comments;
export const selectNoteCommentsCanComment = (state: any, noteId: string | null | undefined): boolean =>
    selectNoteCommentsEntry(state, noteId).canComment;
/** Le propriétaire de la note (C23 SP3) : à lui le geste « Relier à un concept » sur une question. */
export const selectNoteCommentsCanAnswer = (state: any, noteId: string | null | undefined): boolean =>
    selectNoteCommentsEntry(state, noteId).canAnswer;
export const selectNoteCommentsStatus = (state: any, noteId: string | null | undefined): LoadingState =>
    selectNoteCommentsEntry(state, noteId).status;
export const selectNoteCommentsSubmitting = (state: any, noteId: string | null | undefined): boolean =>
    selectNoteCommentsEntry(state, noteId).submitting;
export const selectNoteCommentsError = (state: any, noteId: string | null | undefined): string | null =>
    selectNoteCommentsEntry(state, noteId).error;
/** Commentaires vivants de `noteId` (0 tant que rien n'est chargé pour elle). */
export const selectNoteCommentsCount = (state: any, noteId: string | null | undefined): number =>
    selectNoteCommentsEntry(state, noteId).count;
/** Réactions sur la note elle-même (chantier C10, SP3). */
export const selectNoteReactions = (state: any, noteId: string | null | undefined): ReactionCounts =>
    selectNoteCommentsEntry(state, noteId).noteReactions ?? EMPTY_COUNTS;

export default noteCommentsSlice.reducer;
